import { FC, useCallback, useEffect, useRef } from 'react'

export interface WheelOption {
  value: string
  label: string
}

interface Props {
  options: WheelOption[]
  value: string
  onChange: (value: string) => void
}

const ITEM_HEIGHT = 30
const PADDING = 2

const WheelPicker: FC<Props> = ({ options, value, onChange }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const snapTimer = useRef<ReturnType<typeof setTimeout>>()
  const isProgrammatic = useRef(false)

  const indexOfValue = useCallback(
    (val: string) =>
      Math.max(
        0,
        options.findIndex(o => o.value === val)
      ),
    [options]
  )

  const applyItemStyles = useCallback((activeIdx: number) => {
    if (!scrollRef.current) return
    scrollRef.current.querySelectorAll<HTMLElement>('[data-wheel-idx]').forEach(el => {
      const i = parseInt(el.dataset.wheelIdx!, 10)
      const dist = Math.abs(i - activeIdx)
      if (dist === 0) {
        el.style.opacity = '1'
        el.style.fontSize = '16px'
        el.style.fontWeight = '500'
        el.style.color = 'var(--text-primary)'
      } else if (dist === 1) {
        el.style.opacity = '0.5'
        el.style.fontSize = '15px'
        el.style.fontWeight = '400'
        el.style.color = 'var(--text-secondary)'
      } else {
        el.style.opacity = '0.2'
        el.style.fontSize = '14px'
        el.style.fontWeight = '400'
        el.style.color = 'var(--text-secondary)'
      }
    })
  }, [])

  const scrollToIdx = useCallback(
    (idx: number, behavior: ScrollBehavior = 'smooth') => {
      if (!scrollRef.current) return
      isProgrammatic.current = true
      scrollRef.current.scrollTo({ top: idx * ITEM_HEIGHT, behavior })
      applyItemStyles(idx)
      setTimeout(() => {
        isProgrammatic.current = false
      }, 350)
    },
    [applyItemStyles]
  )

  // Mount: jump to initial position without animation
  useEffect(() => {
    const idx = indexOfValue(value)
    if (scrollRef.current) {
      scrollRef.current.scrollTop = idx * ITEM_HEIGHT
    }
    applyItemStyles(idx)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // External value change → scroll to it
  useEffect(() => {
    if (!isProgrammatic.current) {
      scrollToIdx(indexOfValue(value), 'smooth')
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = useCallback(() => {
    if (isProgrammatic.current || !scrollRef.current) return
    const rawIdx = scrollRef.current.scrollTop / ITEM_HEIGHT
    applyItemStyles(Math.round(rawIdx))

    if (snapTimer.current) clearTimeout(snapTimer.current)
    snapTimer.current = setTimeout(() => {
      if (!scrollRef.current) return
      const final = Math.max(0, Math.min(Math.round(scrollRef.current.scrollTop / ITEM_HEIGHT), options.length - 1))
      const newVal = options[final]?.value
      if (newVal !== value) onChange(newVal)
      isProgrammatic.current = true
      scrollRef.current.scrollTo({ top: final * ITEM_HEIGHT, behavior: 'smooth' })
      setTimeout(() => {
        isProgrammatic.current = false
      }, 350)
    }, 90)
  }, [options, value, onChange, applyItemStyles])

  const totalHeight = ITEM_HEIGHT * (PADDING * 2 + 1)

  return (
    <div style={{ position: 'relative', flex: 1, height: totalHeight, overflow: 'hidden' }}>
      {/* Scroll list */}
      <div
        ref={scrollRef}
        className='wheel-scroll'
        onScroll={handleScroll}
        style={{
          height: '100%',
          overflowY: 'scroll',
          scrollSnapType: 'y mandatory',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div style={{ height: ITEM_HEIGHT * PADDING, flexShrink: 0 }} />
        {options.map((opt, i) => (
          <div
            key={opt.value}
            data-wheel-idx={i}
            onClick={() => {
              onChange(opt.value)
              scrollToIdx(i)
            }}
            style={{
              height: ITEM_HEIGHT,
              scrollSnapAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              opacity: 0.2,
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--text-secondary)',
              transition: 'opacity 0.12s, font-size 0.12s'
            }}
          >
            {opt.label}
          </div>
        ))}
        <div style={{ height: ITEM_HEIGHT * PADDING, flexShrink: 0 }} />
      </div>

      {/* Selection highlight zone */}
      <div
        style={{
          position: 'absolute',
          top: ITEM_HEIGHT * PADDING,
          left: 6,
          right: 6,
          height: ITEM_HEIGHT,
          background: 'rgba(120,120,128,0.1)',
          borderRadius: 8,
          pointerEvents: 'none',
          zIndex: 1
        }}
      />

      {/* Top gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: ITEM_HEIGHT * PADDING,
          background: 'linear-gradient(to bottom, var(--bg-input) 15%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 2
        }}
      />

      {/* Bottom gradient */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: ITEM_HEIGHT * PADDING,
          background: 'linear-gradient(to top, var(--bg-input) 15%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 2
        }}
      />
    </div>
  )
}

export default WheelPicker
